import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listSnapshots,
  createSnapshot,
  deleteSnapshot,
  rollbackSnapshot,
} from "@/api/endpoints/vms";
import type { SnapshotCreatePayload } from "@/types/snapshot";

/**
 * Fetch all Proxmox snapshots for a given VM.
 * The query is disabled when `vmId` is not a positive number.
 *
 * @param vmId - The internal database ID of the VM.
 * @returns A TanStack Query result with an array of snapshot objects.
 */
export function useSnapshots(vmId: number) {
  return useQuery({
    queryKey: ["snapshots", vmId],
    queryFn: () => listSnapshots(vmId),
    enabled: vmId > 0,
  });
}

/**
 * Mutation hook to create a new Proxmox snapshot for a VM.
 * On success, invalidates the snapshot list cache for the VM.
 *
 * @param vmId - The internal database ID of the VM.
 * @returns A TanStack Mutation that accepts a `SnapshotCreatePayload`.
 */
export function useCreateSnapshot(vmId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SnapshotCreatePayload) =>
      createSnapshot(vmId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["snapshots", vmId] }),
  });
}

/**
 * Mutation hook to delete a named Proxmox snapshot from a VM.
 * On success, invalidates the snapshot list cache for the VM.
 *
 * @param vmId - The internal database ID of the VM.
 * @returns A TanStack Mutation that accepts the snapshot name as a string.
 */
export function useDeleteSnapshot(vmId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapName: string) => deleteSnapshot(vmId, snapName),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["snapshots", vmId] }),
  });
}

/**
 * Mutation hook to rollback a VM to a named Proxmox snapshot.
 * On success, invalidates both the snapshot list and the VM list caches,
 * since the VM state may have changed after rollback.
 *
 * @param vmId - The internal database ID of the VM.
 * @returns A TanStack Mutation that accepts the snapshot name as a string.
 */
export function useRollbackSnapshot(vmId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snapName: string) => rollbackSnapshot(vmId, snapName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["snapshots", vmId] });
      qc.invalidateQueries({ queryKey: ["vms"] });
    },
  });
}
